import React, { useState, useEffect } from 'react';

const MaintenanceRequests = ({ onNavigate, userInfo }) => {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [showRequestForm, setShowRequestForm] = useState(false);
    const [formData, setFormData] = useState({
        issueType: '',
        priority: 'medium',
        description: '',
        location: 'Room'
    });

    // State for data from database
    const [requests, setRequests] = useState([]);
    const [statsData, setStatsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [userName, setUserName] = useState('');

    // API Base URL
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in/api';

    const issueTypes = [
        { value: 'electrical', label: 'Electrical Issue', icon: '💡' },
        { value: 'plumbing', label: 'Plumbing Issue', icon: '🚰' },
        { value: 'ac', label: 'AC/Cooling Issue', icon: '❄️' },
        { value: 'furniture', label: 'Furniture Issue', icon: '🪑' },
        { value: 'cleaning', label: 'Cleaning Service', icon: '🧹' },
        { value: 'internet', label: 'Internet/Wi-Fi', icon: '🌐' },
        { value: 'door', label: 'Door/Lock Issue', icon: '🚪' },
        { value: 'other', label: 'Other', icon: '🔧' }
    ];

    // Fetch user name and requests data from backend
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                // Get user ID from userInfo
                const userId = userInfo?.userId || userInfo?.id;

                if (!userId) {
                    throw new Error('User ID not found');
                }

                // Get auth token if available
                const token = localStorage.getItem('token') || sessionStorage.getItem('token');
                const headers = {
                    'Content-Type': 'application/json',
                };

                if (token) {
                    headers['Authorization'] = `Bearer ${token}`;
                }

                // Fetch user profile to get the name
                try {
                    const profileResponse = await fetch(`${API_BASE_URL}/student/profile/${userId}`, {
                        method: 'GET',
                        headers: headers
                    });

                    if (profileResponse.ok) {
                        const profileData = await profileResponse.json();
                        if (profileData.success && profileData.data) {
                            setUserName(profileData.data.fullName || 'User');
                        }
                    }
                } catch (err) {
                    console.warn('Could not fetch user profile:', err);
                }

                // Fetch maintenance requests
                const requestsResponse = await fetch(`${API_BASE_URL}/student/maintenance-requests/${userId}`, {
                    method: 'GET',
                    headers: headers
                });

                if (!requestsResponse.ok) {
                    throw new Error('Failed to fetch maintenance requests');
                }

                const requestsData = await requestsResponse.json();
                
                if (requestsData.success) {
                    // Map the database response to match the component format
                    const mappedRequests = (requestsData.requests || []).map(req => ({
                        id: req.id || req.requestNumber,
                        requestId: req.requestId,
                        issueType: req.issueType,
                        icon: req.icon || getIconForIssueType(req.issueType),
                        description: req.description,
                        location: req.location,
                        priority: req.priority,
                        status: req.status,
                        submittedDate: req.submittedDate,
                        lastUpdate: req.lastUpdate,
                        assignedTo: req.assignedTo || 'Not assigned yet',
                        completedDate: req.completedDate || null
                    }));
                    setRequests(mappedRequests);
                } else {
                    console.warn('Failed to fetch requests:', requestsData.message);
                    setRequests([]);
                }

                // Fetch maintenance request stats
                const statsResponse = await fetch(`${API_BASE_URL}/student/maintenance-requests-stats/${userId}`, {
                    method: 'GET',
                    headers: headers
                });

                if (statsResponse.ok) {
                    const statsResult = await statsResponse.json();
                    if (statsResult.success && statsResult.stats) {
                        const statsInfo = statsResult.stats;
                        setStatsData([
                            { 
                                icon: '📊', 
                                label: 'Total Requests', 
                                value: statsInfo.total.toString(), 
                                subtext: 'This semester' 
                            },
                            { 
                                icon: '⏳', 
                                label: 'Pending', 
                                value: statsInfo.pending.toString(), 
                                subtext: 'Awaiting assignment' 
                            },
                            { 
                                icon: '🔄', 
                                label: 'In Progress', 
                                value: statsInfo.inProgress.toString(), 
                                subtext: 'Being resolved' 
                            },
                            { 
                                icon: '✅', 
                                label: 'Completed', 
                                value: statsInfo.completed.toString(), 
                                subtext: `${statsInfo.total > 0 ? Math.round((statsInfo.completed / statsInfo.total) * 100) : 0}% resolution rate` 
                            }
                        ]);
                    }
                } else {
                    // Use default stats if fetch fails
                    setStatsData([
                        { icon: '📊', label: 'Total Requests', value: '0', subtext: 'This semester' },
                        { icon: '⏳', label: 'Pending', value: '0', subtext: 'Awaiting assignment' },
                        { icon: '🔄', label: 'In Progress', value: '0', subtext: 'Being resolved' },
                        { icon: '✅', label: 'Completed', value: '0', subtext: '0% resolution rate' }
                    ]);
                }

            } catch (err) {
                console.error('Error fetching data:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [userInfo, API_BASE_URL]);

    // Helper function to get icon for issue type
    const getIconForIssueType = (type) => {
        const issueType = issueTypes.find(t => t.value === type?.toLowerCase());
        return issueType ? issueType.icon : '🔧';
    };

    // Use statsData if available, otherwise fallback to default stats
    const stats = statsData || [
        { icon: '📊', label: 'Total Requests', value: '0', subtext: 'This semester' },
        { icon: '⏳', label: 'Pending', value: '0', subtext: 'Awaiting assignment' },
        { icon: '🔄', label: 'In Progress', value: '0', subtext: 'Being resolved' },
        { icon: '✅', label: 'Completed', value: '0', subtext: '0% resolution rate' }
    ];

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        // Validate form data
        if (!formData.issueType || !formData.description.trim()) {
            alert('Please fill in all required fields');
            return;
        }

        try {
            setSubmitting(true);

            const userId = userInfo?.userId || userInfo?.id;
            const token = localStorage.getItem('token') || sessionStorage.getItem('token');

            const headers = {
                'Content-Type': 'application/json',
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            // Submit maintenance request to backend
            const response = await fetch(`${API_BASE_URL}/student/maintenance-requests/${userId}`, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({
                    issueType: formData.issueType,
                    priority: formData.priority,
                    description: formData.description,
                    location: formData.location
                })
            });

            if (!response.ok) {
                throw new Error('Failed to submit request');
            }

            const result = await response.json();

            if (result.success) {
                alert(`Request submitted successfully! Request Number: ${result.requestNumber}`);

                // Reset form
                setFormData({
                    issueType: '',
                    priority: 'medium',
                    description: '',
                    location: 'Room'
                });
                setShowRequestForm(false);

                // Refresh data without full page reload
                const requestsResponse = await fetch(`${API_BASE_URL}/student/maintenance-requests/${userId}`, {
                    method: 'GET',
                    headers: headers
                });

                if (requestsResponse.ok) {
                    const requestsData = await requestsResponse.json();
                    if (requestsData.success) {
                        const mappedRequests = (requestsData.requests || []).map(req => ({
                            id: req.id || req.requestNumber,
                            requestId: req.requestId,
                            issueType: req.issueType,
                            icon: req.icon || getIconForIssueType(req.issueType),
                            description: req.description,
                            location: req.location,
                            priority: req.priority,
                            status: req.status,
                            submittedDate: req.submittedDate,
                            lastUpdate: req.lastUpdate,
                            assignedTo: req.assignedTo || 'Not assigned yet',
                            completedDate: req.completedDate || null
                        }));
                        setRequests(mappedRequests);
                    }
                }

                // Refresh stats
                const statsResponse = await fetch(`${API_BASE_URL}/student/maintenance-requests-stats/${userId}`, {
                    method: 'GET',
                    headers: headers
                });

                if (statsResponse.ok) {
                    const statsResult = await statsResponse.json();
                    if (statsResult.success && statsResult.stats) {
                        const statsInfo = statsResult.stats;
                        setStatsData([
                            { icon: '📊', label: 'Total Requests', value: statsInfo.total.toString(), subtext: 'This semester' },
                            { icon: '⏳', label: 'Pending', value: statsInfo.pending.toString(), subtext: 'Awaiting assignment' },
                            { icon: '🔄', label: 'In Progress', value: statsInfo.inProgress.toString(), subtext: 'Being resolved' },
                            { icon: '✅', label: 'Completed', value: statsInfo.completed.toString(), subtext: `${statsInfo.total > 0 ? Math.round((statsInfo.completed / statsInfo.total) * 100) : 0}% resolution rate` }
                        ]);
                    }
                }
            } else {
                alert(result.message || 'Failed to submit request');
            }

        } catch (error) {
            console.error('Error submitting request:', error);
            alert('Failed to submit request. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const MenuItem = ({ icon, label, active = false, route }) => (
        <div
            className={`menu-item ${active ? 'menu-item-active' : ''}`}
            onClick={() => {
                if (route && onNavigate) {
                    onNavigate(route);
                }
                setSidebarOpen(false);
            }}
        >
            <span className="menu-icon">{icon}</span>
            <span className="menu-label">{label}</span>
            <div className="menu-glow"></div>
        </div>
    );

    const handleLogout = () => {
        if (onNavigate) {
            onNavigate('hostel-login');
        }
    };

    const displayName = userName || userInfo?.name || userInfo?.fullName || 'Student';
    const displayInitial = displayName.charAt(0).toUpperCase();

    // Loading State
    if (loading) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#ffffff',
                flexDirection: 'column',
                gap: '20px'
            }}>
                <div style={{
                    width: '60px',
                    height: '60px',
                    border: '6px solid #f3f3f3',
                    borderTop: '6px solid #2b6777',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                }}></div>
                <p style={{
                    color: '#2b6777',
                    fontSize: '18px',
                    fontWeight: '600'
                }}>Loading Requests...</p>
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    // Error State
    if (error) {
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: '#f4f7fa',
                padding: '2rem'
            }}>
                <div style={{
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    padding: '3rem',
                    textAlign: 'center',
                    maxWidth: '500px',
                    boxShadow: '0 10px 25px rgba(0,0,0,0.1)'
                }}>
                    <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⚠️</div>
                    <h3 style={{ color: '#dc2626', marginBottom: '1rem', fontSize: '1.5rem' }}>
                        Error Loading Requests
                    </h3>
                    <p style={{ color: '#666', marginBottom: '2rem' }}>{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            backgroundColor: '#2b6777',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            padding: '0.75rem 1.5rem',
                            cursor: 'pointer',
                            fontSize: '1rem',
                            fontWeight: '600'
                        }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                body {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
                    -webkit-font-smoothing: antialiased;
                    overflow-x: hidden;
                }

                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                @keyframes shimmer {
                    0% { background-position: -1000px 0; }
                    100% { background-position: 1000px 0; }
                }

                @keyframes pulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                }

                @keyframes glow {
                    0%, 100% { opacity: 0.5; }
                    50% { opacity: 1; }
                }

                .dashboard-container {
                    display: flex;
                    height: 100vh;
                    background: #ffffff;
                    position: relative;
                    overflow: hidden;
                }

                /* Sidebar */
                .sidebar {
                    position: fixed;
                    top: 0;
                    left: 0;
                    bottom: 0;
                    width: 300px;
                    background: linear-gradient(180deg, rgba(255,255,255,0.98) 0%, rgba(242,242,242,0.98) 100%);
                    backdrop-filter: blur(30px);
                    border-right: 2px solid rgba(82,171,152,0.2);
                    transform: translateX(-100%);
                    transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
                    z-index: 100;
                    display: flex;
                    flex-direction: column;
                    box-shadow: 8px 0 40px rgba(43,103,119,0.15);
                }

                .sidebar.open {
                    transform: translateX(0);
                }

                .sidebar-header {
                    padding: 36px 28px;
                    background: linear-gradient(135deg, rgba(43,103,119,0.05) 0%, rgba(82,171,152,0.05) 100%);
                    border-bottom: 2px solid rgba(82,171,152,0.15);
                    position: relative;
                    overflow: hidden;
                }

                .sidebar-header::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: -100%;
                    width: 100%;
                    height: 100%;
                    background: linear-gradient(90deg, transparent, rgba(82,171,152,0.15), transparent);
                    animation: shimmer 4s infinite;
                }

                .sidebar-top {
                    display: flex;
                    align-items: center;
                    justify-content: flex-end;
                    margin-bottom: 24px;
                    position: relative;
                    z-index: 1;
                }

                .close-btn {
                    background: rgba(43,103,119,0.08);
                    border: 1.5px solid rgba(82,171,152,0.25);
                    cursor: pointer;
                    font-size: 20px;
                    color: #2b6777;
                    width: 38px;
                    height: 38px;
                    border-radius: 10px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                }

                .close-btn:hover {
                    background: rgba(43,103,119,0.15);
                    border-color: #52ab98;
                    transform: rotate(90deg);
                }

                .user-info-sidebar {
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    position: relative;
                    z-index: 1;
                }

                .user-avatar-sidebar {
                    width: 56px;
                    height: 56px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 800;
                    font-size: 22px;
                    color: white;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                    border: 3px solid rgba(255, 255, 255, 0.5);
                }

                .user-details-sidebar {
                    flex: 1;
                }

                .user-name-sidebar {
                    font-size: 16px;
                    font-weight: 800;
                    color: #2b6777;
                    margin-bottom: 4px;
                }

                .user-role-sidebar {
                    font-size: 13px;
                    color: #52ab98;
                    font-weight: 600;
                }

                .sidebar-nav {
                    flex: 1;
                    padding: 28px 20px;
                    overflow-y: auto;
                }

                .sidebar-nav::-webkit-scrollbar {
                    width: 6px;
                }

                .sidebar-nav::-webkit-scrollbar-track {
                    background: rgba(82,171,152,0.08);
                }

                .sidebar-nav::-webkit-scrollbar-thumb {
                    background: rgba(82,171,152,0.3);
                    border-radius: 3px;
                }

                .menu-item {
                    position: relative;
                    display: flex;
                    align-items: center;
                    gap: 14px;
                    padding: 16px 18px;
                    margin-bottom: 10px;
                    cursor: pointer;
                    color: #2b6777;
                    font-size: 15px;
                    font-weight: 600;
                    border-radius: 14px;
                    transition: all 0.3s;
                    overflow: hidden;
                }

                .menu-glow {
                    position: absolute;
                    inset: 0;
                    background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
                    opacity: 0;
                    transition: opacity 0.3s;
                }

                .menu-item:hover {
                    color: #52ab98;
                    background: rgba(82,171,152,0.1);
                    transform: translateX(6px);
                    box-shadow: 0 4px 16px rgba(82,171,152,0.15);
                }

                .menu-item:hover .menu-glow {
                    opacity: 1;
                }

                .menu-item-active {
                    background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
                    color: #2b6777;
                    box-shadow: 0 6px 20px rgba(82,171,152,0.2);
                    font-weight: 700;
                }

                .menu-item-active .menu-glow {
                    opacity: 1;
                    animation: glow 2s ease-in-out infinite;
                }

                .menu-icon {
                    font-size: 22px;
                    position: relative;
                    z-index: 1;
                }

                .menu-label {
                    position: relative;
                    z-index: 1;
                }

                .logout-section {
                    padding: 20px;
                    border-top: 2px solid rgba(82,171,152,0.15);
                }

                .logout-btn {
                    width: 100%;
                    padding: 16px 20px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    color: white;
                    border: none;
                    border-radius: 14px;
                    font-size: 15px;
                    font-weight: 700;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 10px;
                    transition: all 0.3s;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                }

                .logout-btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 10px 35px rgba(43,103,119,0.4);
                }

                /* Overlay */
                .overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(43,103,119,0.4);
                    backdrop-filter: blur(6px);
                    z-index: 90;
                    opacity: 0;
                    visibility: hidden;
                    transition: all 0.3s ease;
                }

                .overlay.show {
                    opacity: 1;
                    visibility: visible;
                }

                /* Main Content */
                .main-content {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    width: 100%;
                    position: relative;
                    z-index: 1;
                }

                /* Top Header */
                .top-header {
                    background: #ffffff;
                    padding: 24px 20px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    border-bottom: 1px solid rgba(82,171,152,0.12);
                    position: relative;
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                }

                .top-header::after {
                    content: '';
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    right: 0;
                    height: 3px;
                    background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
                }

                .header-left {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    flex: 1;
                }

                .menu-toggle {
                    background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
                    border: 2px solid rgba(82,171,152,0.25);
                    cursor: pointer;
                    font-size: 24px;
                    color: #2b6777;
                    width: 44px;
                    height: 44px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.3s;
                    box-shadow: 0 4px 20px rgba(43,103,119,0.15);
                    flex-shrink: 0;
                }

                .menu-toggle:hover {
                    transform: scale(1.05);
                    box-shadow: 0 6px 30px rgba(43,103,119,0.25);
                    background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
                }

                .header-title-section {
                    display: flex;
                    flex-direction: column;
                    gap: 4px;
                    min-width: 0;
                    flex: 1;
                }

                .header-greeting {
                    font-size: 13px;
                    color: #52ab98;
                    font-weight: 600;
                    display: none;
                }

                .header-name {
                    font-size: 18px;
                    font-weight: 900;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }

                .header-right {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    flex-shrink: 0;
                }

                .user-avatar-header {
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: 800;
                    font-size: 18px;
                    color: white;
                    cursor: pointer;
                    border: 3px solid rgba(82,171,152,0.3);
                    transition: all 0.3s;
                    box-shadow: 0 6px 24px rgba(43,103,119,0.3);
                }

                .user-avatar-header:hover {
                    transform: scale(1.1);
                    box-shadow: 0 8px 32px rgba(43,103,119,0.5);
                }

                /* Content Wrapper */
                .content-wrapper {
                    flex: 1;
                    overflow-y: auto;
                    padding: 20px 16px;
                }

                .content-wrapper::-webkit-scrollbar {
                    width: 8px;
                }

                .content-wrapper::-webkit-scrollbar-track {
                    background: rgba(82,171,152,0.08);
                }

                .content-wrapper::-webkit-scrollbar-thumb {
                    background: rgba(82,171,152,0.3);
                    border-radius: 4px;
                }

                .content-area {
                    max-width: 1500px;
                    margin: 0 auto;
                    animation: slideIn 0.6s ease-out;
                }

                /* Hero Section */
                .hero-section {
                    background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
                    border-radius: 12px;
                    padding: 28px 20px;
                    margin-bottom: 24px;
                    border: 1px solid rgba(82,171,152,0.15);
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                    position: relative;
                    overflow: hidden;
                }

                .hero-section::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    height: 4px;
                    background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
                }

                .hero-content {
                    position: relative;
                    z-index: 1;
                }

                .hero-title {
                    font-size: 24px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 8px;
                    letter-spacing: -0.5px;
                }

                .hero-subtitle {
                    font-size: 13px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 500;
                }

                /* Stats Grid */
                .stats-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 12px;
                    margin-bottom: 24px;
                }

                .stat-card {
                    background: #ffffff;
                    border-radius: 12px;
                    padding: 20px 16px;
                    border: 1px solid rgba(43,103,119,0.12);
                    cursor: pointer;
                    transition: all 0.3s ease;
                    position: relative;
                    overflow: hidden;
                    box-shadow: 0 2px 8px rgba(43,103,119,0.06);
                }

                .stat-card::before {
                    content: '';
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 4px;
                    height: 100%;
                    background: linear-gradient(180deg, #2b6777 0%, #52ab98 100%);
                    transform: scaleY(0);
                    transition: transform 0.3s ease;
                }

                .stat-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 24px rgba(43,103,119,0.12);
                    border-color: rgba(82,171,152,0.25);
                }

                .stat-card:hover::before {
                    transform: scaleY(1);
                }

                .stat-header {
                    display: flex;
                    align-items: flex-start;
                    justify-content: space-between;
                    margin-bottom: 16px;
                }

                .stat-label {
                    font-size: 10px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 600;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .stat-icon {
                    width: 36px;
                    height: 36px;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 18px;
                    background: rgba(82,171,152,0.08);
                    border: 1px solid rgba(82,171,152,0.15);
                }

                .stat-value {
                    font-size: 26px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 6px;
                    letter-spacing: -0.5px;
                    line-height: 1;
                }

                .stat-subtext {
                    font-size: 11px;
                    color: rgba(43,103,119,0.5);
                    font-weight: 500;
                }

                /* Section */
                .section {
                    background: rgba(255,255,255,0.9);
                    backdrop-filter: blur(30px);
                    border-radius: 16px;
                    padding: 24px 20px;
                    margin-bottom: 24px;
                    border: 2px solid rgba(82,171,152,0.2);
                    box-shadow: 0 4px 16px rgba(43,103,119,0.08);
                }

                .section-header {
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    margin-bottom: 20px;
                    padding-bottom: 16px;
                    border-bottom: 2px solid rgba(82,171,152,0.15);
                    flex-wrap: wrap;
                    gap: 12px;
                }

                .section-title-group {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                }

                .section-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    border: 2px solid rgba(82,171,152,0.3);
                    color: white;
                    box-shadow: 0 4px 16px rgba(43,103,119,0.2);
                    flex-shrink: 0;
                }

                .section-title {
                    font-size: 18px;
                    font-weight: 900;
                    color: #2b6777;
                }

                .new-request-btn {
                    padding: 12px 24px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    color: white;
                    border: none;
                    border-radius: 10px;
                    font-size: 14px;
                    font-weight: 800;
                    cursor: pointer;
                    transition: all 0.3s;
                    box-shadow: 0 4px 16px rgba(43,103,119,0.3);
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }

                .new-request-btn:hover {
                    transform: translateY(-3px);
                    box-shadow: 0 8px 24px rgba(43,103,119,0.4);
                }

                /* Request Form */
                .request-form {
                    background: rgba(255,255,255,0.9);
                    backdrop-filter: blur(30px);
                    border-radius: 16px;
                    padding: 24px 20px;
                    margin-bottom: 24px;
                    border: 2px solid rgba(82,171,152,0.2);
                    box-shadow: 0 4px 16px rgba(43,103,119,0.08);
                }

                .form-title {
                    font-size: 18px;
                    font-weight: 900;
                    color: #2b6777;
                    margin-bottom: 20px;
                }

                .form-group {
                    margin-bottom: 16px;
                }

                .form-label {
                    display: block;
                    margin-bottom: 8px;
                    font-size: 14px;
                    font-weight: 600;
                    color: #334155;
                }

                .form-input, .form-select, .form-textarea {
                    width: 100%;
                    padding: 10px 12px;
                    border: 1px solid rgba(82,171,152,0.2);
                    border-radius: 8px;
                    font-size: 14px;
                    color: #1E293B;
                    outline: none;
                    transition: all 0.3s;
                    font-family: inherit;
                }

                .form-input:focus, .form-select:focus, .form-textarea:focus {
                    border-color: #52ab98;
                    box-shadow: 0 0 0 3px rgba(82,171,152,0.1);
                }

                .form-textarea {
                    resize: vertical;
                    min-height: 80px;
                }

                .form-actions {
                    display: flex;
                    gap: 12px;
                    justify-content: flex-end;
                    margin-top: 20px;
                    flex-wrap: wrap;
                }

                .btn-secondary {
                    padding: 10px 20px;
                    background: rgba(82,171,152,0.1);
                    color: #2b6777;
                    border: 1.5px solid rgba(82,171,152,0.25);
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s;
                }

                .btn-secondary:hover {
                    background: rgba(82,171,152,0.2);
                    border-color: #52ab98;
                }

                .btn-primary {
                    padding: 10px 20px;
                    background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
                    color: white;
                    border: none;
                    border-radius: 8px;
                    font-size: 14px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.3s;
                    box-shadow: 0 4px 16px rgba(43,103,119,0.3);
                }

                .btn-primary:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 6px 20px rgba(43,103,119,0.4);
                }

                .btn-primary:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                    transform: none;
                }

                /* Requests List */
                .requests-list {
                    display: grid;
                    gap: 16px;
                }

                .request-card {
                    background: rgba(255,255,255,0.9);
                    backdrop-filter: blur(30px);
                    border-radius: 16px;
                    padding: 20px;
                    border: 2px solid rgba(82,171,152,0.2);
                    box-shadow: 0 4px 16px rgba(43,103,119,0.08);
                    transition: all 0.3s;
                }

                .request-card:hover {
                    transform: translateY(-4px);
                    box-shadow: 0 8px 24px rgba(43,103,119,0.12);
                    border-color: rgba(82,171,152,0.3);
                }

                .request-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 12px;
                }

                .request-main-info {
                    display: flex;
                    gap: 12px;
                    align-items: center;
                    flex: 1;
                }

                .request-icon {
                    font-size: 28px;
                    flex-shrink: 0;
                }

                .request-details {
                    flex: 1;
                }

                .request-title {
                    font-size: 16px;
                    font-weight: 800;
                    color: #2b6777;
                    margin-bottom: 4px;
                }

                .request-description {
                    font-size: 14px;
                    color: rgba(43,103,119,0.7);
                    font-weight: 500;
                    margin-bottom: 8px;
                }

                .request-meta {
                    display: flex;
                    gap: 16px;
                    font-size: 12px;
                    color: rgba(43,103,119,0.6);
                    font-weight: 600;
                }

                .request-status {
                    padding: 6px 12px;
                    border-radius: 20px;
                    font-size: 11px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .status-pending {
                    background: rgba(255,193,7,0.1);
                    color: #f57c00;
                }

                .status-in-progress {
                    background: rgba(33,150,243,0.1);
                    color: #2196f3;
                }

                .status-completed {
                    background: rgba(76,175,80,0.1);
                    color: #4caf50;
                }

                .status-cancelled {
                    background: rgba(244,67,54,0.1);
                    color: #f44336;
                }

                .request-priority {
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-size: 10px;
                    font-weight: 700;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                }

                .priority-low {
                    background: rgba(76,175,80,0.1);
                    color: #4caf50;
                }

                .priority-medium {
                    background: rgba(255,193,7,0.1);
                    color: #f57c00;
                }

                .priority-high {
                    background: rgba(244,67,54,0.1);
                    color: #f44336;
                }

                .request-footer {
                    margin-top: 16px;
                    padding-top: 12px;
                    border-top: 1px solid rgba(82,171,152,0.15);
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    font-size: 12px;
                    color: rgba(43,103,119,0.6);
                }

                .empty-state {
                    text-align: center;
                    padding: 60px 20px;
                    color: rgba(43,103,119,0.5);
                }

                .empty-icon {
                    font-size: 48px;
                    margin-bottom: 16px;
                }

                .empty-title {
                    font-size: 18px;
                    font-weight: 700;
                    color: #2b6777;
                    margin-bottom: 8px;
                }

                .empty-text {
                    font-size: 14px;
                    color: rgba(43,103,119,0.6);
                }

                /* Responsive Design */
                @media (min-width: 768px) {
                    .top-header {
                        padding: 32px 24px;
                    }

                    .header-name {
                        font-size: 22px;
                    }

                    .header-greeting {
                        display: block;
                        font-size: 14px;
                    }

                    .header-title-section {
                        flex-direction: row;
                        align-items: center;
                        gap: 12px;
                    }

                    .content-wrapper {
                        padding: 32px 24px;
                    }

                    .hero-section {
                        padding: 48px 28px;
                    }

                    .hero-title {
                        font-size: 32px;
                    }

                    .hero-subtitle {
                        font-size: 15px;
                    }

                    .stats-grid {
                        grid-template-columns: repeat(4, 1fr);
                        gap: 16px;
                    }

                    .stat-card {
                        padding: 24px 20px;
                    }

                    .section {
                        padding: 32px 28px;
                    }

                    .section-header {
                        gap: 16px;
                        margin-bottom: 28px;
                    }

                    .section-icon {
                        width: 56px;
                        height: 56px;
                        font-size: 28px;
                    }

                    .section-title {
                        font-size: 22px;
                    }

                    .request-form {
                        padding: 32px 28px;
                    }

                    .form-actions {
                        gap: 16px;
                    }

                    .requests-list {
                        gap: 20px;
                    }

                    .request-card {
                        padding: 24px;
                    }

                    .request-meta {
                        gap: 24px;
                    }
                }

                @media (min-width: 1024px) {
                    .sidebar {
                        position: static;
                        transform: translateX(0);
                    }

                    .close-btn {
                        display: none;
                    }

                    .overlay {
                        display: none !important;
                    }

                    .menu-toggle {
                        display: none;
                    }

                    .top-header {
                        padding: 52px 32px;
                    }

                    .header-name {
                        font-size: 28px;
                    }

                    .header-greeting {
                        font-size: 18px;
                    }

                    .content-wrapper {
                        padding: 56px 48px;
                    }

                    .hero-section {
                        padding: 90px 36px;
                    }

                    .hero-title {
                        font-size: 36px;
                    }

                    .hero-subtitle {
                        font-size: 16px;
                    }

                    .stats-grid {
                        gap: 20px;
                    }

                    .stat-card {
                        padding: 28px;
                    }

                    .stat-label {
                        font-size: 12px;
                    }

                    .stat-icon {
                        width: 48px;
                        height: 48px;
                        font-size: 24px;
                    }

                    .stat-value {
                        font-size: 32px;
                    }

                    .stat-subtext {
                        font-size: 13px;
                    }

                    .section {
                        padding: 40px;
                        border-radius: 28px;
                    }

                    .section-header {
                        gap: 20px;
                        margin-bottom: 32px;
                        padding-bottom: 24px;
                    }

                    .section-icon {
                        width: 64px;
                        height: 64px;
                        border-radius: 18px;
                        font-size: 32px;
                    }

                    .section-title {
                        font-size: 28px;
                    }

                    .request-form {
                        padding: 40px;
                        border-radius: 28px;
                    }

                    .form-title {
                        font-size: 22px;
                    }

                    .new-request-btn {
                        padding: 14px 32px;
                        font-size: 15px;
                    }

                    .btn-primary, .btn-secondary {
                        padding: 12px 24px;
                        font-size: 15px;
                    }

                    .request-card {
                        padding: 28px;
                        border-radius: 28px;
                    }

                    .request-title {
                        font-size: 18px;
                    }

                    .request-description {
                        font-size: 15px;
                    }

                    .request-meta {
                        font-size: 13px;
                    }
                }

                @media (max-width: 380px) {
                    .header-name {
                        font-size: 16px;
                    }

                    .hero-title {
                        font-size: 20px;
                    }

                    .stat-value {
                        font-size: 22px;
                    }

                    .section-title {
                        font-size: 16px;
                    }

                    .new-request-btn {
                        padding: 10px 16px;
                        font-size: 13px;
                    }

                    .form-actions {
                        flex-direction: column;
                    }

                    .btn-primary, .btn-secondary {
                        width: 100%;
                    }

                    .request-meta {
                        flex-direction: column;
                        gap: 8px;
                    }
                }
            `}</style>

            <div className="dashboard-container">
                <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
                    <div className="sidebar-header">
                        <div className="sidebar-top">
                            <button onClick={() => setSidebarOpen(false)} className="close-btn">
                                ✕
                            </button>
                        </div>
                        <div className="user-info-sidebar">
                            <div className="user-avatar-sidebar">
                                {displayInitial}
                            </div>
                            <div className="user-details-sidebar">
                                <div className="user-name-sidebar">{displayName}</div>
                                <div className="user-role-sidebar">Student Portal</div>
                            </div>
                        </div>
                    </div>

                    <nav className="sidebar-nav">
                        <MenuItem icon="🏠" label="Dashboard" route="hostel-student-dashboard" />
                        <MenuItem icon="🚪" label="Entry/Exit Log" route="hostel-student-entry-exit-log" />
                        <MenuItem icon="🛏" label="Room Details" route="hostel-student-room-details" />
                        <MenuItem icon="🍽️" label="Food Preference" route="hostel-student-food-preference" />
                        <MenuItem icon="🧺" label="Laundry Service" route="hostel-student-laundry" />
                        <MenuItem icon="💳" label="Grievance" route="hostel-student-payments" />
                        <MenuItem icon="📝" label="Requests" active={true} route="hostel-student-requests" />
                        <MenuItem icon="📋" label="Requisition" route="hostel-student-requisition" />
                    </nav>

                    <div className="logout-section">
                        <button onClick={handleLogout} className="logout-btn">
                            🚪 Logout
                        </button>
                    </div>
                </div>

                <div
                    className={`overlay ${sidebarOpen ? 'show' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                />

                <div className="main-content">
                    <div className="top-header">
                        <div className="header-left">
                            <button
                                className="menu-toggle"
                                onClick={() => setSidebarOpen(true)}
                            >
                                ☰
                            </button>
                            <div className="header-title-section">
                                <div className="header-greeting">Maintenance Requests</div>
                                <div className="header-name">{displayName}</div>
                            </div>
                        </div>
                        <div className="header-right">
                            <div className="user-avatar-header">
                                {displayInitial}
                            </div>
                        </div>
                    </div>

                    <div className="content-wrapper">
                        <div className="content-area">
                            <div className="hero-section">
                                <div className="hero-content">
                                    <h1 className="hero-title">Maintenance Requests 🔧</h1>
                                    <p className="hero-subtitle">Submit and track your maintenance requests</p>
                                </div>
                            </div>

                            <div className="stats-grid">
                                {stats.map((stat, index) => (
                                    <div key={index} className="stat-card">
                                        <div className="stat-header">
                                            <div className="stat-label">{stat.label}</div>
                                            <div className="stat-icon">{stat.icon}</div>
                                        </div>
                                        <div className="stat-value">{stat.value}</div>
                                        <div className="stat-subtext">{stat.subtext}</div>
                                    </div>
                                ))}
                            </div>

                            <div className="section">
                                <div className="section-header">
                                    <div className="section-title-group">
                                        <div className="section-icon">📋</div>
                                        <div className="section-title">Your Requests</div>
                                    </div>
                                    <button
                                        className="new-request-btn"
                                        onClick={() => setShowRequestForm(true)}
                                    >
                                        <span>+</span> New Request
                                    </button>
                                </div>

                                {showRequestForm && (
                                    <div className="request-form">
                                        <div className="form-title">Submit New Maintenance Request</div>

                                        <div className="form-group">
                                            <label className="form-label">Issue Type *</label>
                                            <select
                                                className="form-select"
                                                name="issueType"
                                                value={formData.issueType}
                                                onChange={handleInputChange}
                                            >
                                                <option value="">Select Issue Type</option>
                                                {issueTypes.map((type) => (
                                                    <option key={type.value} value={type.value}>
                                                        {type.icon} {type.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Priority</label>
                                            <select
                                                className="form-select"
                                                name="priority"
                                                value={formData.priority}
                                                onChange={handleInputChange}
                                            >
                                                <option value="low">Low</option>
                                                <option value="medium">Medium</option>
                                                <option value="high">High</option>
                                            </select>
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Location</label>
                                            <select
                                                className="form-select"
                                                name="location"
                                                value={formData.location}
                                                onChange={handleInputChange}
                                            >
                                                <option value="Room">Room</option>
                                                <option value="Common Area">Common Area</option>
                                                <option value="Bathroom">Bathroom</option>
                                                <option value="Corridor">Corridor</option>
                                                <option value="Other">Other</option>
                                            </select>
                                        </div>

                                        <div className="form-group">
                                            <label className="form-label">Description *</label>
                                            <textarea
                                                className="form-textarea"
                                                name="description"
                                                value={formData.description}
                                                onChange={handleInputChange}
                                                placeholder="Please describe the issue in detail..."
                                                rows={4}
                                            />
                                        </div>

                                        <div className="form-actions">
                                            <button
                                                className="btn-secondary"
                                                onClick={() => setShowRequestForm(false)}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                className="btn-primary"
                                                onClick={handleSubmit}
                                                disabled={submitting}
                                            >
                                                {submitting ? 'Submitting...' : 'Submit Request'}
                                            </button>
                                        </div>
                                    </div>
                                )}

                                <div className="requests-list">
                                    {requests.length > 0 ? (
                                        requests.map((request) => (
                                            <div key={request.id} className="request-card">
                                                <div className="request-header">
                                                    <div className="request-main-info">
                                                        <div className="request-icon">{request.icon}</div>
                                                        <div className="request-details">
                                                            <div className="request-title">
                                                                {request.issueType} - #{request.id}
                                                            </div>
                                                            <div className="request-description">
                                                                {request.description}
                                                            </div>
                                                            <div className="request-meta">
                                                                <span>📍 {request.location}</span>
                                                                <span>📅 {request.submittedDate}</span>
                                                                <span>⏰ {request.lastUpdate}</span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                                                        <div className={`request-status status-${request.status.toLowerCase().replace(' ', '-')}`}>
                                                            {request.status}
                                                        </div>
                                                        <div className={`request-priority priority-${request.priority}`}>
                                                            {request.priority}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="request-footer">
                                                    <div>Assigned to: {request.assignedTo}</div>
                                                    {request.completedDate && (
                                                        <div>Completed: {request.completedDate}</div>
                                                    )}
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="empty-state">
                                            <div className="empty-icon">📋</div>
                                            <div className="empty-title">No Requests Yet</div>
                                            <div className="empty-text">
                                                You haven't submitted any maintenance requests yet. Click "New Request" to get started.
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default MaintenanceRequests;